import { BreadcrumbOptions } from "@/components/breadcrumbs";
import { DomainList, DomainListItemCard } from "@/components/domain";
import { Skeleton } from "@/components/ui/skeleton";
import { useDeleteServiceProvider } from "@/hooks/service-providers/delete.hooks";
import { useGetServiceProviders } from "@/hooks/service-providers/get.hooks";
import { T } from "@/i18n";
import { cn } from "@/lib/utils";
import { ServiceProvider, ServiceProviderDomain } from "@/models";
import { ConciergeBell, Star, User } from "lucide-react";
import { Key, ReactNode } from "react";
import { useTranslation } from "react-i18next";

function getBreadCrumbsForService(
  name: string,
  id: string,
  loading: boolean,
): BreadcrumbOptions[] {
  return [
    {
      content: (
        <>
          <T tKey="services.title" /> <ConciergeBell width={16} height={16} />
        </>
      ),
      href: "/services",
    },
    {
      content: (
        <>
          {loading ? (
            <Skeleton className="h-5 w-10" />
          ) : (
            <>
              {name} <ConciergeBell width={16} height={16} />
            </>
          )}
        </>
      ),
      href: "/services/$id",
      params: { id },
    },
    {
      content: (
        <>
          <T tKey="providers.title" /> <User width={16} height={16} />
        </>
      ),
      href: "/providers",
    },
  ];
}

function getBreadCrumbsForProvider(
  name: string,
  id: string,
  loading: boolean,
): BreadcrumbOptions[] {
  return [
    {
      content: (
        <>
          <T tKey="providers.title" /> <User width={16} height={16} />
        </>
      ),
      href: "/providers",
    },
    {
      content: (
        <>
          {loading ? (
            <Skeleton className="h-5 w-10" />
          ) : (
            <>
              {name}
              <User width={16} height={16} />
            </>
          )}
        </>
      ),
      href: "/providers/$id",
      params: { id },
    },
    {
      content: (
        <>
          <T tKey="services.title" /> <ConciergeBell width={16} height={16} />
        </>
      ),
      href: "/services",
    },
  ];
}

const breadcrumbsForDomain: Record<
  ServiceProviderDomain,
  (name: string, id: string, loading: boolean) => BreadcrumbOptions[]
> = {
  service: getBreadCrumbsForService,
  provider: getBreadCrumbsForProvider,
};

type LinkFn = (id: string, children: ReactNode, key: Key) => JSX.Element;

export function ServiceProvidersPage({
  domain,
  domainId,
  domainLoading,
  domainName,
  editLink,
}: {
  domain: ServiceProviderDomain;
  domainId: string;
  domainName?: string;
  domainLoading: boolean;
  editLink: LinkFn;
}) {
  const query = useGetServiceProviders(domain, domainId);

  return (
    <DomainList
      domain={`serviceProviders.${domain}`}
      breadcrumbs={breadcrumbsForDomain[domain](
        domainName ?? "",
        domainId,
        domainLoading,
      )}
      tableOptions={{
        hasActions: true,
        columns: [
          {
            header:
              domain === "provider"
                ? "providers.fields.name"
                : "services.fields.name",
          },
          {
            header: "serviceProviders.fields.rating",
          },
          {
            header: "serviceProviders.fields.regionId",
          },
          {
            header: "serviceProviders.fields.requiresPrice",
          },
          {
            header: "serviceProviders.fields.price",
          },
        ],
      }}
      type="table"
      data={query.data}
      isLoading={query.isLoading}
      newLink="/service-providers/$id/new"
      newLinkParams={{ id: domainId }}
      newLinkSearch={{ domain }}
      renderItem={(serviceProvider, index, isTable) => (
        <ServiceProviderCard
          isTable={isTable}
          editLink={editLink}
          domain={domain}
          domainId={domainId}
          serviceProvider={serviceProvider}
          key={index}
          name={
            domain === "service"
              ? (serviceProvider.provider?.name ?? "-")
              : (serviceProvider.service?.name ?? "-")
          }
        />
      )}
    />
  );
}

function ServiceProviderCard({
  serviceProvider,
  name,
  domain,
  domainId,
  editLink,
  isTable,
}: {
  serviceProvider: ServiceProvider;
  name: string;
  domain: ServiceProviderDomain;
  domainId: string;
  editLink: LinkFn;
  isTable: boolean;
}) {
  const { mutateAsync } = useDeleteServiceProvider(domain, domainId);
  const deleteServiceProvider = () =>
    mutateAsync(serviceProvider.id.toString());
  const { t } = useTranslation();
  return (
    <DomainListItemCard
      table={isTable}
      domain={`serviceProviders.${domain}`}
      title={name}
      titleTableOptions={{
        tableClassName: "whitespace-nowrap",
      }}
      description={<RatingList value={serviceProvider.rating} />}
      editLink={(children, key) => editLink(serviceProvider.id, children, key)}
      contentMap={[
        {
          label: t("serviceProviders.fields.regionId"),
          value: serviceProvider.region?.name ?? "-",
          tableClassName: "whitespace-nowrap",
        },
        {
          label: t("serviceProviders.fields.requiresPrice"),
          value: (
            <T tKey={`common.boolean.${!!serviceProvider.requiresPrice}`} />
          ),
        },
        {
          label: t("serviceProviders.fields.price"),
          value: serviceProvider.price ?? "-",
        },
      ]}
      onDelete={deleteServiceProvider}
    />
  );
}

function RatingList({ value }: { value: number }) {
  return (
    <div className="flex items-center justify-start gap-1">
      {Array.from({ length: 10 })
        .fill(0)
        .map((_, i) => (
          <Star
            key={i}
            className={cn(
              "aspect-square w-3 stroke-yellow-500",
              i < value ? "fill-yellow-500" : "",
            )}
          />
        ))}
    </div>
  );
}
